import { OutlineElement } from '../../base/outline-element/outline-element';
import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import componentStyles from './tufts-header.css.lit';
// import { TuftsMenu } from '../tufts-menu/tufts-menu';
import GuiDialog from '../../../utilities/dialog';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import logoMarkup from './logo-markup';
import '../tufts-menu/tufts-menu';
import '../tufts-container/tufts-container';
// import '../tufts-location/tufts-location';
import '../tufts-icon/tufts-icon';
import '../tufts-heading/tufts-heading';
import '../tufts-button/tufts-button';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { SlotNGController } from '../../controllers/slot-ng-controller';
import { ResizeController } from '../../controllers/resize-controller';
import { debounce } from '../../../utilities/debounce';

/**
 * The Tufts Header Component
 *
 * @element tufts-header
 *
 * @slot translate
 *
 * @attr debug-menu
 * @attr menu-id
 * @attr menu-name
 * @attr menu-lang
 * @attr menu-type
 * @attr hamburger-menu-open
 * @attr hamburger-menu-name
 * @attr mobile-mode
 * @attr group-name-label
 * @attr group-menu-id
 * @attr no-group-label
 * @attr zipcode-label
 * @attr find-button-label
 * @attr current-language
 * @attr translate-label
 * @attr en-url
 * @attr es-url
 * @attr main-menu-label
 * @attr utility-menu-label
 * @attr account-menu-1-label
 * @attr account-menu-1-url
 * @attr account-menu-2-label
 * @attr account-menu-2-url
 * @attr donate-url
 * @attr donate-label
 * @attr en-label
 * @attr es-label
 * @attr search-url
 * @attr search-placeholder
 * @attr menu-label
 * @attr group-menu-label
 * @attr chapter-url
 * @attr warning-text
 */

@customElement('tufts-header')
export class TuftsHeader extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  ngSlot = new SlotNGController(this);
  resizeController: ResizeController = new ResizeController(this, {
    breakpoints: [1024],
  });

  @query('.mobile-menu-container')
  mobileMenu: HTMLElement;

  /**
   * Wether to use real from URL or debug menu.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'debug-menu',
  })
  debugMenu: boolean;

  /**
   * Menu machine name.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'menu-id',
  })
  menuId = 'main-menu';

  /**
   * Menu full name (for screen-readers).
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'menu-name',
  })
  menuName = 'Main Menu';

  /**
   * Menu language.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'menu-lang',
  })
  menuLang = '';

  /**
   * Menu Type - 'menu' or 'groupcontentmenu'.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'menu-type',
  })
  menuType = 'menu';

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'hamburger-menu-open',
  })
  hamburgerMenuOpen: boolean;

  @property({
    type: String,
    reflect: true,
    attribute: 'hamburger-menu-name',
  })
  hamburgerMenuName = 'main menu';

  /**
   * Force mobile menu (vertical) display.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'mobile-mode',
  })
  isMobile = false;

  // isSticky = false;

  @property({
    type: String,
    reflect: true,
    attribute: 'group-name-label',
  })
  groupNameLabel: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'group-menu-id',
  })
  groupMenuId: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'no-group-label',
  })
  noGroupLabel: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'zipcode-label',
  })
  zipcodeLabel: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'find-button-label',
  })
  findButtonLabel: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'current-language',
  })
  currentLanguage: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'translate-label',
  })
  translateLabel: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'en-url',
  })
  enURL: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'es-url',
  })
  esURL: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'main-menu-label',
  })
  mainMenuLabel: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'utility-menu-label',
  })
  utilityMenuLabel: string;

  // @property({
  //   type: String,
  //   reflect: true,
  //   attribute: 'location-menu-label',
  // })
  // locationMenuLabel: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'account-menu-1-label',
  })
  accountMenu1Label: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'account-menu-1-url',
  })
  accountMenu1URL: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'account-menu-2-label',
  })
  accountMenu2Label: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'account-menu-2-url',
  })
  accountMenu2URL: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'donate-url',
  })
  donateURL: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'donate-label',
  })
  donateLabel: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'en-label',
  })
  enLabel: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'es-label',
  })
  esLabel: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'search-label',
  })
  searchLabel = 'Search';

  @property({
    type: String,
    reflect: true,
    attribute: 'search-url',
  })
  searchURL = 'search';

  @property({
    type: String,
    reflect: true,
    attribute: 'search-placeholder',
  })
  searchPlaceholder: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'menu-label',
  })
  menuLabel: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'group-menu-label',
  })
  groupMenuLabel: string;

  // @property({
  //   type: Boolean,
  //   reflect: true,
  //   attribute: 'location-finder-open',
  // })
  // locationFinderOpen: boolean;

  @property({
    type: String,
    reflect: true,
    attribute: 'chapter-url',
  })
  chapterUrl: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'json-main-menu',
  })
  jsonMainMenu: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'json-header-utility-menu',
  })
  jsonHeaderUtilityMenu: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'json-header-menu-menu',
  })
  jsonHeaderMenuMenu: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'json-feature-links-menu',
  })
  jsonFeatureLinksMenu: string;

  @state() searchDropdownStatus = false;

  firstUpdated() {
    // this.stickyMode();
  }

  // USE THIS IF WE WANT TO GO BACK TO ANIMATE MENU (WIP FOR LATER)
  // updated() {
  //   if (!this.hamburgerMenuOpen) {
  //     this.collapseSection(this.mobileMenu);
  //   }
  // }

  requestsBufferMap = new Map<string, boolean>();

  /*
   * Set body padding as CSS variable to account for sticky header (padding itself gets applied in shared.css)
   */
  setBodyPadding() {
    // Let menu render first, and then measure '.sticky-header'
    setTimeout(() => {
      const stickyHeaderHeight =
        this.shadowRoot?.querySelector('.sticky-header')?.clientHeight;

      if (stickyHeaderHeight) {
        document.body.style.setProperty(
          '--sticky-header-height',
          stickyHeaderHeight + 'px'
        );
      } else {
        document.body.style.removeProperty('--sticky-header-height');
      }
    }, 0);
  }

  /*
   * Create a Map for any menu that request mobile mode
   */
  private processRequests(menuId: string, mode: string) {
    if (mode === 'mobile') {
      this.requestsBufferMap.set(menuId, true);
    } else if (this.requestsBufferMap.has(menuId)) {
      this.requestsBufferMap.delete(menuId);
    }

    // set header mobile mode if at least 1 menu requires mobile mode
    this.isMobile = this.requestsBufferMap.size > 0;
    this.setBodyPadding();
  }

  private _menuModeListener(e: CustomEvent) {
    this.processRequests(e.detail.menuId, e.detail.mode);
  }

  _trapKeyboardMobileMenu(event: KeyboardEvent) {
    if (event.key === 'Tab' && !event.shiftKey) {
      // After opening mobile menu, next tab will focus on first menu item. This also traps the keyboard to go through menu items until hamburger menu is closed.

      // Only trap the keyboard if the active element is the menu dropdown
      if (
        !document.activeElement?.shadowRoot?.activeElement?.matches(
          'button.menu-dropdown'
        )
      ) {
        return;
      }

      // Get all focusable elements in the menu
      const focusableElements =
        this.shadowRoot!.querySelector(
          'tufts-menu'
        )!.shadowRoot!.querySelectorAll('a[href], button');

      if (!focusableElements) {
        return;
      }
      event.preventDefault();

      const firstFocusableElement = focusableElements[0] as HTMLElement;
      firstFocusableElement.focus();
    }
  }

  _trapKeyboardMobileSearch(event: KeyboardEvent) {
    if (event.key !== 'Tab') {
      return;
    }

    // Get the search button
    const searchButton = this.shadowRoot!.querySelector(
      '.open-search-button'
    ) as HTMLElement;

    // Get all focusable elements in the menu
    const focusableElements = this.shadowRoot
      ?.querySelector('#search-wrapper')
      ?.querySelectorAll('a[href], button, input');

    // If focusable elements are returned, then get the first and last elements
    if (focusableElements) {
      const firstSearchPanelElement = focusableElements[0] as HTMLElement;
      const lastSearchPanelElement = focusableElements[
        focusableElements.length - 1
      ] as HTMLElement;

      // If the active element is the search button, then the next tab will go to the first element in the search panel. If the active element is the last element in the panel, the next tab will go to the search button.
      if (document.activeElement?.shadowRoot?.activeElement === searchButton) {
        event.preventDefault();
        firstSearchPanelElement.focus();
      } else if (
        document.activeElement?.shadowRoot?.activeElement ===
        lastSearchPanelElement
      ) {
        event.preventDefault();
        searchButton!.focus();
      }
    }
  }

  checkAndCloseMobileMenu() {
    // Check if not in mobile mode
    if (!this.isMobile) {
      const bodyElement = document.querySelector('body');

      // Check if body has style overflow: hidden
      if (
        bodyElement &&
        window.getComputedStyle(bodyElement).overflow === 'hidden'
      ) {
        // Find the menu dropdown button
        const menuDropdownButton = document
          .querySelector('outline-header')
          ?.shadowRoot?.querySelector(
            'button.menu-dropdown'
          ) as HTMLButtonElement;

        // If the button exists, simulate a click to close the menu and remove body style overflow: hidden
        if (menuDropdownButton) {
          menuDropdownButton.click();
        }
      }
    }
  }

  render() {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;
    this.checkAndCloseMobileMenu();
    this.setBodyPadding();
    return html`
      <div
        @menuMode=${this._menuModeListener}
        class=${classMap({
          mobile: this.isMobile,
          header: true,
          // 'is-sticky': this.isSticky,
        })}
      >
        <div class="tufts-main-header">
          ${this.isMobile ? this.mobileTemplate() : this.desktopTemplate()}
          <div id="piwik-consent"></div>
          <div
            id="piwik-consent-band"
            class="piwik-consent-band${this.isMobile ? '-mobile' : null}"
          >
            <div class="piwik-content">
              <span>
                We use <a href="#" id="piwik-consent-band-open">cookies</a> to
                improve your experience on our website. You can learn more about
                what information we collect and how we use it in our
                <a href="/website-privacy-policy">Privacy Policy</a>.
              </span>
              <div class="piwik-cta-container">
                <tufts-button
                  button-variant="compact"
                  button-url="#"
                  button-label="Accept all"
                  icon-type=""
                  id="piwik-consent-accept"
                >
                  Accept all
                </tufts-button>
                <tufts-button
                  button-variant="compact"
                  button-url="#"
                  button-label="Decline all"
                  icon-type=""
                  id="piwik-consent-decline"
                >
                  Decline all
                </tufts-button>
              </div>
            </div>
            <tufts-icon
              id="piwik-consent-band-close"
              name="x"
              library="system"
              size="48px"
            ></tufts-icon>
          </div>
        </div>
        ${this.isMobile ? this.stickyMobileTemplate() : ''}
      </div>
    `;
  }

  toggleHamburgerMenu() {
    // USE THIS IF WE WANT TO GO BACK TO ANIMATE MENU (WIP FOR LATER)
    // this.hamburgerMenuOpen
    //   ? this.collapseSection(this.mobileMenu)
    //   : this.expandSection(this.mobileMenu);
    this.hamburgerMenuOpen = !this.hamburgerMenuOpen;
    if (this.hamburgerMenuOpen) {
      this.searchDropdownStatus = false;
      document.querySelector('body')!.style.overflow = 'hidden';
      this._trapKeyboardMobileMenu = this._trapKeyboardMobileMenu.bind(this);
      document.addEventListener('keydown', this._trapKeyboardMobileMenu);
    } else {
      document.querySelector('body')!.style.overflow = 'revert';
      document.removeEventListener('keydown', this._trapKeyboardMobileMenu);
      this.removeBlurEffect();
    }
  }

  topMobileTemplate() {
    return html` ${this.logoTemplate()} ${this.loginTemplate()}`;
  }

  loginTemplate() {
    return html`
      <a class="mobile-login" href="https://www.mytuftsmed.org/">
        <tufts-icon name="login" size="1.5rem" library="system"></tufts-icon>
        Login
      </a>
    `;
  }

  mobileMenuTriggerTemplate() {
    return html` <button
      class="menu-dropdown"
      @click="${this.toggleHamburgerMenu}"
      aria-label="Main Menu"
      aria-expanded="${this.hamburgerMenuOpen}"
      aria-controls="sliding-header"
    >
      <tufts-icon
        name="${this.hamburgerMenuOpen ? 'close-mobile' : 'duo-menu'}"
        size="1.25rem"
        library="system"
      ></tufts-icon>
      <span class="sticky-mobile-item menu-label"
        >${this.hamburgerMenuOpen ? 'Close' : 'Menu'}
      </span>
    </button>`;
  }

  mobileSearchTriggerTemplate() {
    return html` <button
      type="button"
      class="open-search-button"
      aria-expanded="${this.searchDropdownStatus}"
      aria-controls="#search-wrapper"
      @click="${this.toggleMobileSearchDropdown}"
    >
      <tufts-icon
        name="${this.searchDropdownStatus ? 'close-mobile' : 'duo-search'}"
        size="1.25rem"
        library="system"
      ></tufts-icon>
      <span class="sticky-mobile-item search-label"
        >${this.searchDropdownStatus ? 'Close' : this.searchLabel}
      </span>
    </button>`;
  }

  stickyMobileTemplate() {
    return html` <div class="sticky-header">
      ${this.mobileMenuTriggerTemplate()}
      <a class="sticky-mobile-item locations-menu" href="/get-care/locations">
        <tufts-icon
          name="locations"
          size="1.25em"
          library="system"
        ></tufts-icon>
        Locations
      </a>
      <a class="sticky-mobile-item doctors-menu" href="/find-a-doctor">
        <tufts-icon name="doctors" size="1.25em" library="system"></tufts-icon>
        Doctors
      </a>
      ${this.mobileSearchTriggerTemplate()}
    </div>`;
  }

  mobileTemplate() {
    return html`
      <tufts-container padding="space-0" background-color="white">
        <div class="top-header">${this.topMobileTemplate()}</div>
      </tufts-container>
      <div
        id="sliding-header"
        class="sliding-header ${this.hamburgerMenuOpen ? 'is-active' : ''}"
      >
        <tufts-container padding="space-0" background-color="white">
          <div class="top-header">${this.topMobileTemplate()}</div>
        </tufts-container>
        <div class="hamburger-dropdown">
          <tufts-container padding="space-m" background-color="transparent">
            ${this.mainMenuTemplate()}
            <hr class="horizontal-divider" />
            ${this.utilityMenuTemplate()}
          </tufts-container>
        </div>
      </div>
      <div
        id="sliding-search"
        class="sliding-search ${this.searchDropdownStatus ? 'is-active' : ''}"
      >
        <tufts-container padding="space-0" background-color="white">
          <div class="top-header">${this.topMobileTemplate()}</div>
        </tufts-container>
        <div class="search-dropdown">
          <tufts-container padding="space-m" background-color="transparent">
            <div id="search-wrapper" class="search-wrapper">
              ${this.searchFormTemplate()} ${this.searchFormSuggestionLinks()}
            </div>
          </tufts-container>
        </div>
      </div>
    `;
  }

  // LEAVING THE BELOW FUNCTIONS IN IN CASE WE WANT TO ANIMATE THE MENU DROPDOWN LATER.

  // collapseSection(element: HTMLElement) {
  //   element.style.maxHeight = '0px';
  //   //isMobile && (element.style.overflow = 'hidden');
  //   setTimeout(() => {
  //     element.ariaHidden = 'true';
  //     element.style.visibility = 'hidden';
  //   }, 250);
  // }

  // expandSection(element: HTMLElement) {
  //   // get the height of the element's inner content and the height of any possible
  //   // nested sub dropdown's inner content, regardless of their actual size
  //   element.ariaHidden = 'false';
  //   let totalScrollHeight = 0;
  //   element.style.visibility = 'visible';
  //   const locationMenuHeight =
  //     element.querySelector('#utility-menu')?.scrollHeight;

  //   const menus = element
  //     .querySelector('tufts-menu')
  //     ?.shadowRoot?.querySelectorAll('.menu-level-ul');

  //   if (menus)
  //     menus.forEach(child => {
  //       //(child as HTMLElement).style.visibility = 'visible';
  //       const subDropDownHeight: number | undefined = child.scrollHeight;
  //       totalScrollHeight += subDropDownHeight ? subDropDownHeight : 0;
  //     });

  //   element.style.maxHeight =
  //     (locationMenuHeight ? locationMenuHeight : 0) + totalScrollHeight + 'px';
  //   // isMobile && setTimeout(() => (element.style.overflow = 'visible'), 100);
  // }

  desktopTemplate() {
    return html`
      <tufts-container
        background-color="blue_dark"
        container-width="wide"
        padding="space-3xs-2xs"
      >
        ${this.utilityMenuTemplate()}
      </tufts-container>

      <tufts-container
        background-color="${this.extraMenuOpen ? 'blue_dark' : 'white'}"
        data-search-button
      >
        <div class="header-wrapper">
          ${this.logoTemplate()}
          <div class="main-menu-wrapper">
            ${this.mainMenuTemplate()} ${this.searchDropdownDesktopTemplate()}
          </div>
        </div>
      </tufts-container>
    `;
  }

  logoTemplate() {
    return html` <a href="/" title="Home" class="logo">
      ${unsafeHTML(logoMarkup)}
    </a>`;
  }

  mainMenuTemplate() {
    return html`
      <tufts-menu
        json=${ifDefined(this.jsonMainMenu)}
        ?mobile="${this.isMobile}"
        ?debug-menu="${this.debugMenu}"
        menu-id="main-menu"
        depth="2"
        menu-name="${this.menuName}"
        menu-label="${this.mainMenuLabel}"
        menu-lang="${this.currentLanguage}"
        menu-heading-size="xs"
        mobile-trigger="true"
        color-mode="light"
        justify="right"
        top-menu-no-link
      ></tufts-menu>
    `;
  }

  utilityMenuTemplate() {
    return html`
      <tufts-menu
        json=${ifDefined(this.jsonHeaderUtilityMenu)}
        ?mobile="${this.isMobile}"
        ?debug-menu="${this.debugMenu}"
        menu-id="header-utility"
        menu-name="utility"
        menu-label="Utility"
        mobile-trigger="true"
        color-mode="dark"
        justify="right"
      ></tufts-menu>
    `;
  }

  @state() extraMenuOpen = false;
  toggleExtraMenu() {
    this.extraMenuOpen = !this.extraMenuOpen;
  }

  headerMenuTemplate() {
    return html`
      <tufts-menu
        json=${ifDefined(this.jsonHeaderMenuMenu)}
        ?mobile="${this.isMobile}"
        ?debug-menu="${this.debugMenu}"
        menu-id="header-menu"
        menu-name="header-menu"
        menu-label="Header menu"
        menu-heading-size="xs"
        color-mode="dark"
      ></tufts-menu>
    `;
  }

  @state() translateMenuOpen = false;
  openTranslateMenu() {
    this.translateMenuOpen = true;
  }
  closeTranslateMenu() {
    this.translateMenuOpen = false;
  }
  toggleTranslateMenu(e: KeyboardEvent) {
    e.key === 'Enter' && (this.translateMenuOpen = !this.translateMenuOpen);
    e.key === 'Escape' && (this.translateMenuOpen = false);
  }
  translateMenuTemplate() {
    return html`
      <div
        class="translate-menu-button-container"
        @mouseenter="${this.openTranslateMenu}"
        @mouseleave="${this.closeTranslateMenu}"
        @keydown="${this.toggleTranslateMenu}"
      >
        <button
          class="no-title dropdown-button"
          type="button"
          aria-expanded="${this.translateMenuOpen}"
          aria-haspopup="true"
          tabindex="0"
        >
          ${this.translateLabel}
          <span class="dropdown-icon">
            <tufts-icon
              name="chevron-down"
              library="system"
              size="1rem"
            ></tufts-icon>
          </span>
        </button>
        <div class="translate-menu">
          <ul>
            <li><a href="${this.enURL}">${this.enLabel}</a></li>
            <li><a href="${this.esURL}">${this.esLabel}</a></li>
          </ul>
          <div class="google-translate">
            ${this.ngSlot.renderInShadow('translate')}
            <div class="google-watermark">
              Powered by
              <tufts-icon
                library="system"
                name="google-logo"
                size-width="3rem"
              ></tufts-icon>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  searchFormTemplate(): TemplateResult {
    return html`
      <form action="/search" method="get" accept-charset="UTF-8">
        <label for="search-input" class="visually-hidden"
          >${this.searchPlaceholder}</label
        >
        <div class="input-wrapper">
          <input
            type="search"
            placeholder="${this.isMobile ? '' : 'Search by keyword or phrase'}"
            id="search-input"
            name="text"
            value=""
            size="30"
            maxlength="128"
          />

          <button
            type="submit"
            value="Search"
            @click="${this.handleSearchClick}"
          >
            <tufts-icon
              name="magnifying-glass"
              size="1.5rem"
              library="system"
            ></tufts-icon>
            <span class="visually-hidden">${this.searchLabel}</span>
          </button>
        </div>
      </form>
      ${this.showLoading ? this.displayPending() : null}
    `;
  }
  @state() showLoading = false;
  private escapeListenerAdded = false;
  handleSearchClick() {
    this.showLoading = true;
    if (!this.escapeListenerAdded) {
      this._handleEscapeKey = this._handleEscapeKey.bind(this);
      document.addEventListener('keydown', this._handleEscapeKey);
      this.escapeListenerAdded = true;
    }
  }
  _handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.showLoading = false;
      document.removeEventListener('keydown', this._handleEscapeKey);
      this.escapeListenerAdded = false;
    }
  }

  displayPending() {
    return html`
      <div class="lds-ring">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    `;
  }

  mobileSearchTemplate() {
    return html`
      <tufts-container padding="space-0" background-color="white">
        <div class="top-header">${this.topMobileTemplate()}</div>
      </tufts-container>
      <div
        id="sliding-search"
        class="sliding-search ${this.hamburgerMenuOpen ? 'is-active' : ''}"
      >
        <tufts-container padding="space-0" background-color="white">
          <div class="top-header">${this.topMobileTemplate()}</div>
        </tufts-container>
        <div class="hamburger-dropdown">
          <tufts-container padding="space-m" background-color="transparent">
            ${this.mainMenuTemplate()}
            <hr class="horizontal-divider" />
            ${this.utilityMenuTemplate()}
          </tufts-container>
        </div>
      </div>
    `;
  }

  suggestionLinksTemplate(searchKeywords: string, link: string) {
    return html`
      <li>
        <a
          class="suggestion-link"
          href="/services-treatments/${link}"
          @click="${this.handleSearchClick}"
        >
          ${searchKeywords}
        </a>
      </li>
    `;
  }

  searchFormSuggestionLinks() {
    return html`
      <div class="search-suggestions">
        <span class="suggestion-group-label"> Common searches </span>
        <ul>
          ${this.suggestionLinksTemplate('Primary care', 'primary-care')}
          ${this.suggestionLinksTemplate(
            'Cardiology',
            'cardiovascular/cardiology'
          )}
          ${this.suggestionLinksTemplate(
            'OBGYN',
            'obstetrics-gynecology-obgyn'
          )}
          ${this.suggestionLinksTemplate('Neurology', 'neurology')}
          ${this.suggestionLinksTemplate('Dermatology', 'dermatology')}
          ${this.suggestionLinksTemplate(
            'Weight loss',
            'weight-wellness/bariatric-weight-loss-surgery'
          )}
          ${this.suggestionLinksTemplate(
            'Gastroenterology',
            'gastroenterology-digestive-health'
          )}
          ${this.suggestionLinksTemplate('Orthopedics', 'orthopedics')}
        </ul>
      </div>
    `;
  }

  searchDropdownDesktopTemplate() {
    return html` ${this.searchDialogTemplate()} `;
  }

  searchDropdownMobileTemplate() {
    return html` ${this.searchDropdownTriggerTemplate()} `;
  }

  toggleMobileSearchDropdown() {
    this.searchDropdownStatus = !this.searchDropdownStatus;

    if (this.searchDropdownStatus) {
      this.hamburgerMenuOpen = false;
      document.querySelector('body')!.style.overflow = 'hidden';
      this._trapKeyboardMobileSearch =
        this._trapKeyboardMobileSearch.bind(this);
      document.addEventListener('keydown', this._trapKeyboardMobileSearch);
    } else {
      document.querySelector('body')!.style.overflow = 'revert';
      document.removeEventListener('keydown', this._trapKeyboardMobileSearch);
      this.removeBlurEffect();
    }
  }

  removeBlurEffect() {
    const mainContent = document.querySelector('main');
    if (mainContent) mainContent.classList.remove('blur-effect');
  }

  toggleDesktopSearchDropdown() {
    this.searchDropdownStatus = !this.searchDropdownStatus;
    const searchPanel =
      document.activeElement?.shadowRoot?.activeElement?.nextElementSibling;

    const focusableElements = searchPanel?.querySelectorAll(
      'input, a[href], button'
    );

    if (focusableElements) {
      const firstFocusableElement = focusableElements[0] as HTMLElement;
      const lastFocusableElement = focusableElements[
        focusableElements.length - 1
      ] as HTMLElement;

      if (this.searchDropdownStatus) {
        this.handleKeydown = this.handleKeydown.bind(this);
        firstFocusableElement.addEventListener('keydown', this.handleKeydown);
        lastFocusableElement.addEventListener('keydown', this.handleKeydown);
        this.removeBlurEffect();
      } else {
        firstFocusableElement.removeEventListener(
          'keydown',
          this.handleKeydown
        );
        lastFocusableElement.removeEventListener('keydown', this.handleKeydown);
      }
    }
  }

  handleKeydown(event: KeyboardEvent) {
    const firstFocusableElement = event.currentTarget as HTMLElement;
    const searchPanel = firstFocusableElement.closest('#search-wrapper');
    const focusableElements = searchPanel?.querySelectorAll(
      'input, a[href], button'
    );
    const lastFocusableElement = focusableElements?.[
      focusableElements.length - 1
    ] as HTMLElement;

    if (event.key === 'Tab') {
      if (event.shiftKey && event.target === firstFocusableElement) {
        // Shift+Tab on the first element
        event.preventDefault();
        this.closeSearch();
      } else if (!event.shiftKey && event.target === lastFocusableElement) {
        // Tab on the last element
        event.preventDefault();
        this.closeSearch();
      }
    }
  }

  closeSearch() {
    const searchPanel =
      document.activeElement?.shadowRoot?.activeElement?.nextElementSibling;
    searchPanel?.removeEventListener('mouseleave', this.closeSearch);
    const searchToggleButton =
      searchPanel?.closest('#search-wrapper')?.previousElementSibling;
    searchToggleButton?.setAttribute('aria-expanded', 'false');
    this.searchDropdownStatus = false;
    // Clean up keyboard event listeners
    const focusableElements = searchPanel?.querySelectorAll(
      'input, a[href], button'
    );
    if (focusableElements) {
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[
        focusableElements.length - 1
      ] as HTMLElement;
      firstElement?.removeEventListener('keydown', this.handleKeydown);
      lastElement?.removeEventListener('keydown', this.handleKeydown);
    }
  }

  searchDropdownTriggerTemplate() {
    const mobileSearchIcon = this.searchDropdownStatus
      ? 'close'
      : 'magnifying-glass';
    return html`
      <button
        type="button"
        class="open-search-button"
        aria-expanded="${this.searchDropdownStatus}"
        aria-controls="#search-wrapper"
        @click="${this.toggleDesktopSearchDropdown}"
      >
        <tufts-icon
          name="${this.isMobile ? 'duo-search' : mobileSearchIcon}"
          size="${this.isMobile ? '1.25rem' : '1.5rem'}"
          library="system"
        ></tufts-icon>
        <span class="search-label ${!this.isMobile ? 'visually-hidden' : null}"
          >${this.searchLabel}</span
        >
      </button>
    `;
  }

  searchDialogTemplate(): TemplateResult {
    const mobileSearchIcon = this.searchDropdownStatus
      ? ''
      : 'magnifying-glass';
    return html`
      <button
        class="open-search-button"
        data-dialog-id="search-dialog"
        @click="${this.openDialog}"
      >
        <tufts-icon
          name="${this.isMobile ? 'duo-search' : mobileSearchIcon}"
          size="${this.isMobile ? '1.25rem' : '1.5rem'}"
          library="system"
        ></tufts-icon>
        <span class="search-label ${!this.isMobile ? 'visually-hidden' : null}"
          >${this.searchLabel}</span
        >
      </button>
      <dialog id="search-dialog">
        <div id="search-wrapper" class="search-wrapper">
          <button
            class="close-search-button"
            type="button"
            @click="${this.closeDialog}"
          >
            <tufts-icon
              name="close"
              library="system"
              size="1.5rem"
            ></tufts-icon>
          </button>
          ${this.searchFormTemplate()} ${this.searchFormSuggestionLinks()}
        </div>
      </dialog>
    `;
  }

  openDialog(e: Event) {
    const trigger = e.currentTarget as HTMLDialogElement;
    const dialogElement = trigger.nextElementSibling as HTMLDialogElement;
    const input = trigger.parentElement?.querySelector(
      '#search-input'
    ) as HTMLInputElement;
    input.value = '';

    // Calculate the position of the button relative to the viewport
    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dialogHeight = dialogElement.offsetHeight;

    // Check if dialog would overflow viewport bottom
    const topPosition = rect.bottom + 8;
    const bottomOverflow = topPosition + dialogHeight > viewportHeight;

    // Position above button if it would overflow bottom
    const finalPosition = bottomOverflow
      ? rect.top - dialogHeight - 8
      : topPosition;

    // Set the dialog's position
    dialogElement.style.position = 'fixed';
    dialogElement.style.top = `${Math.max(8, finalPosition)}px`;

    // Debounced update of dialog position on window resize
    const updatePosition = debounce(() => {
      const newRect = trigger.getBoundingClientRect();
      const newTopPosition = newRect.bottom + 8;
      const newBottomOverflow = newTopPosition + dialogHeight > viewportHeight;
      const newFinalPosition = newBottomOverflow
        ? newRect.top - dialogHeight - 8
        : newTopPosition;
      dialogElement.style.top = `${Math.max(8, newFinalPosition)}px`;
    }, 150);

    window.addEventListener('resize', updatePosition);
    dialogElement.addEventListener('close', () => {
      window.removeEventListener('resize', updatePosition);
    });

    this.searchDropdownStatus = true;
    GuiDialog(dialogElement);
    dialogElement.showModal();
    // Add event listener for the 'close' event
    dialogElement.addEventListener('close', this.handleDialogClose);
  }

  handleDialogClose = (e: Event) => {
    const dialogElement = e.currentTarget as HTMLDialogElement;
    this.searchDropdownStatus = false;

    // Remove the event listener for the 'close' event
    dialogElement.removeEventListener('close', this.handleDialogClose);
  };

  closeDialog(e: Event) {
    const trigger = e.currentTarget as HTMLDialogElement;
    const dialogElement = trigger.closest('dialog') as HTMLDialogElement;
    dialogElement.close('cancel');
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    const dialogElement = this.shadowRoot?.querySelector('#search-dialog');
    if (dialogElement) {
      dialogElement.removeEventListener('close', this.handleDialogClose);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-header': TuftsHeader;
  }
}
